import React, { useState } from 'react';

interface LandingPageProps {
  onGetStarted: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted }) => {
  const [activeDemo, setActiveDemo] = useState(0);

  const demoSlides = [
    {
      title: "ダッシュボード",
      description: "月額合計：¥3,480",
      content: "Netflix ¥1,490 | Spotify ¥980 | Adobe ¥1,010"
    },
    {
      title: "支払いアラート", 
      description: "明日支払い予定",
      content: "Netflix（月額プラン） - 2025/06/26"
    },
    {
      title: "詳細分析",
      description: "累計支払額",
      content: "Netflix: ¥17,880（12ヶ月継続）"
    }
  ];

  const handleDemoClick = () => {
    setActiveDemo((prev) => (prev + 1) % demoSlides.length);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* ヘッダー */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-600">
                SubscNavi
              </div>
            </div>
            <button
              onClick={onGetStarted}
              className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-full font-medium hover:bg-blue-700 transition-colors text-sm sm:text-base touch-manipulation"
            >
              ログイン
            </button>
          </div>
        </div>
      </header>

      {/* ヒーローセクション */}
      <section className="pt-8 sm:pt-16 pb-12 sm:pb-20 relative overflow-hidden">
        {/* 背景装飾 */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-5 sm:top-10 left-5 sm:left-10 w-16 sm:w-32 h-16 sm:h-32 bg-blue-200 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute top-16 sm:top-32 right-10 sm:right-20 w-10 sm:w-20 h-10 sm:h-20 bg-purple-200 rounded-full opacity-30 animate-bounce delay-150"></div>
          <div className="absolute bottom-10 sm:bottom-20 left-10 sm:left-20 w-12 sm:w-24 h-12 sm:h-24 bg-green-200 rounded-full opacity-25 animate-pulse delay-300"></div>
          <div className="absolute bottom-16 sm:bottom-32 right-16 sm:right-32 w-8 sm:w-16 h-8 sm:h-16 bg-orange-200 rounded-full opacity-20 animate-bounce delay-500"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              もう
              <span className="text-blue-600 relative">
                サブスク迷子
                <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full h-2 sm:h-3" viewBox="0 0 300 12" fill="none">
                  <path d="M5 6C50 2, 100 10, 150 6C200 2, 250 10, 295 6" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
                </svg>
              </span>
              <br className="sm:hidden" />にならない
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto">
              大学生のための、シンプル＆クリーンなサブスクリプション管理アプリ
            </p>
            <p className="text-base sm:text-lg text-gray-500 mb-8 sm:mb-12 max-w-2xl mx-auto">
              一度登録すれば、次回支払日を自動で予測。「今月いくら払っているか」「支払いが近いサービス」をひと目で把握できます。
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
              <button
                onClick={onGetStarted}
                className="group bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center gap-2 touch-manipulation"
              >
                <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                無料で始める
                <span className="transform group-hover:translate-x-1 transition-all duration-300">→</span>
              </button>
              <button
                onClick={handleDemoClick}
                className="group border-2 border-gray-300 text-gray-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium hover:border-blue-400 hover:text-blue-600 transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center gap-2 touch-manipulation"
              >
                <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M19 10a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                デモを見る
              </button>
            </div>
          </div>

          {/* デモセクション */}
          <div className="mt-16 max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-6 border">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {demoSlides[activeDemo].title}
                </h3>
                <p className="text-blue-600 font-medium">
                  {demoSlides[activeDemo].description}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-gray-700">
                  {demoSlides[activeDemo].content}
                </p>
              </div>
              <div className="flex justify-center mt-4 gap-2">
                {demoSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveDemo(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === activeDemo ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 特徴セクション */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              こんな悩みありませんか？
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                サブスクが多すぎて
              </h3>
              <p className="text-gray-600">
                Netflix、Spotify、Adobe...気づいたら10個以上のサービスに登録していた
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                月額料金が分からない
              </h3>
              <p className="text-gray-600">
                「今月いくら払ってるんだろう？」毎月の支出が把握できない
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                更新日を忘れる
              </h3>
              <p className="text-gray-600">
                「あ、また課金されてる...」いつの間にか料金が引き落とされている
              </p>
            </div>
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              SubscNaviが解決します
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-blue-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 mx-auto mb-4 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                ダッシュボード
              </h3>
              <p className="text-gray-600 text-sm">
                月額合計コストをリアルタイム表示
              </p>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 mx-auto mb-4 bg-purple-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM12.01 17A7.5 7.5 0 1112 2.5a7.5 7.5 0 01.01 14.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                支払いアラート
              </h3>
              <p className="text-gray-600 text-sm">
                次回支払日が近いサービスを通知
              </p>
            </div>
            <div className="bg-green-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 mx-auto mb-4 bg-green-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                自動計算
              </h3>
              <p className="text-gray-600 text-sm">
                次回支払日を自動で予測・更新
              </p>
            </div>
            <div className="bg-orange-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 mx-auto mb-4 bg-orange-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                カテゴリ管理
              </h3>
              <p className="text-gray-600 text-sm">
                サービスを分類して整理
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 使い方セクション */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              シンプルな3ステップ
            </h2>
            <p className="text-xl text-gray-600">
              面倒な設定は一切なし。誰でも簡単に始められます
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                サービスを登録
              </h3>
              <p className="text-gray-600">
                Netflix、Spotify等、使っているサービスを登録。料金と支払いサイクルを入力するだけ
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                自動で計算
              </h3>
              <p className="text-gray-600">
                次回支払日と月額合計を自動計算。継続月数や累計支払額も分析
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                ひと目で把握
              </h3>
              <p className="text-gray-600">
                ダッシュボードで支出状況を確認。支払いが近いサービスもすぐわかる
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTAセクション */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700 relative overflow-hidden">
        {/* 背景装飾 */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full opacity-5 transform -translate-y-32"></div>
          <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-white rounded-full opacity-10 transform translate-y-24"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6l9-5-9-5-9 5 9 5z" />
              </svg>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              サブスク管理、今すぐ始めませんか？
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              無料で使えて、個人情報も最小限。大学生にやさしいシンプル設計
            </p>
          </div>
          <button
            onClick={onGetStarted}
            className="group bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-medium hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center justify-center gap-2 mx-auto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            無料で始める
            <span className="transform group-hover:translate-x-1 transition-all duration-300">→</span>
          </button>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-2xl font-bold mb-4">
              SubscNavi
            </div>
            <p className="text-gray-400 mb-4">
              大学生のためのサブスクリプション管理アプリ
            </p>
            <div className="text-sm text-gray-500">
              © 2025 SubscNavi. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
